import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment-timezone';
import { fetchDataFromAPI } from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ/api/dataGridApiService';
import type {
  FilterTrayCategory,
  FilterTrayOption,
  FilterTraySelection,
} from 'app/components/ReusableUIComponents/HighLevelComponents/FilterTrayMAIQ';
import { formatToDealerTime } from 'utils/dateUtils';

const JOURNEYS_ENDPOINT = 'api/v2/nurturing-automation/opportunities';
const FILTER_TRAY_PAGE_SIZE = 500;

/**
 * Quick-filter categories supported on the Nurturing Journeys grid, in display order.
 * Mirrors the opportunities row fields used as query-param filters by the backend.
 */
const JOURNEY_FIELD_KEYS = [
  'test_date',
  'last_retry_date',
  'segment',
  'nurturing_stage',
] as const;
type JourneyFieldKey = (typeof JOURNEY_FIELD_KEYS)[number];

/** Human-friendly labels for each category surfaced in the FilterTray sidebar. */
const JOURNEY_FIELD_LABELS: Record<JourneyFieldKey, string> = {
  last_retry_date: 'Last Retry Date',
  segment: 'Segment',
  nurturing_stage: 'Stage',
  test_date: 'Test Date',
};

function toStr(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** Normalized API `test_date` used as the filter value sent to the backend. */
function normalizeTestDateValue(value: unknown): string {
  return toStr(value).trim();
}

interface JourneyValueMaps {
  /** Set of distinct option values per field key. */
  valuesByField: Record<JourneyFieldKey, Set<string>>;
  /**
   * For fields where the on-screen label differs from the filter value (e.g.
   * `test_date`), this map carries the display string keyed by option value.
   */
  labelByValue: Record<JourneyFieldKey, Map<string, string>>;
}

function emptyValueMaps(): JourneyValueMaps {
  return {
    valuesByField: {
      last_retry_date: new Set(),
      segment: new Set(),
      nurturing_stage: new Set(),
      test_date: new Set(),
    },
    labelByValue: {
      last_retry_date: new Map(),
      segment: new Map(),
      nurturing_stage: new Map(),
      test_date: new Map(),
    },
  };
}

function deriveValueMaps(rows: any[]): JourneyValueMaps {
  const maps = emptyValueMaps();
  for (const row of rows ?? []) {
    if (!row || typeof row !== 'object') continue;

    const segment = toStr(row.segment).trim();
    if (segment) maps.valuesByField.segment.add(segment);

    // Filter is sent under the underlying `nurturing_stage` key (backend
    // contract), but show the friendlier `nurturing_stage_label` to users when
    // available so options read e.g. "Retargeting" instead of an internal code.
    const stageValue = toStr(row.nurturing_stage).trim();
    if (stageValue) {
      maps.valuesByField.nurturing_stage.add(stageValue);
      if (!maps.labelByValue.nurturing_stage.has(stageValue)) {
        const stageLabel = toStr(row.nurturing_stage_label).trim() || stageValue;
        maps.labelByValue.nurturing_stage.set(stageValue, stageLabel);
      }
    }

    const testDateValue = normalizeTestDateValue(row.test_date);
    if (testDateValue) {
      maps.valuesByField.test_date.add(testDateValue);
      if (!maps.labelByValue.test_date.has(testDateValue)) {
        const label = formatToDealerTime(testDateValue) || testDateValue;
        maps.labelByValue.test_date.set(testDateValue, label);
      }
    }

    const lastRetryDateValue = normalizeTestDateValue(row.last_retry_date);
    if (lastRetryDateValue) {
      maps.valuesByField.last_retry_date.add(lastRetryDateValue);
      if (!maps.labelByValue.last_retry_date.has(lastRetryDateValue)) {
        const label = formatToDealerTime(lastRetryDateValue) || lastRetryDateValue;
        maps.labelByValue.last_retry_date.set(lastRetryDateValue, label);
      }
    }
  }
  return maps;
}

export interface UseTestNurturingJourneysFilterTrayArgs {
  dealerId: string;
  opportunityType?: string;
  /** Only fetch the (separate) options query when the panel is open. Grid loads via DataGridMAIQ regardless. */
  fetchEnabled?: boolean;
}

export interface UseTestNurturingJourneysFilterTrayResult {
  categories: FilterTrayCategory[];
  optionsForActiveCategory: FilterTrayOption[];
  selectedByCategory: FilterTraySelection;
  onSelectionChange: (categoryId: string, selectedValues: string[]) => void;
  onClearAllFilters: () => void;
  onClearActiveCategory: () => void;
  onClearAllInPanel: () => void;
  /** Filters keyed by API field name, ready to feed into `TEST_NURTURING_JOURNEYS_LEADS`. */
  metadataFilters: Record<string, string[]>;
  totalActiveCount: number;
  activeCategoryId: string | null;
  setActiveCategoryId: (id: string | null) => void;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Drives the Nurturing Journeys Quick Filter tray. Refetches opportunities with
 * current selections applied (cascading options like {@link useEmailTemplatesFilterTray}),
 * then derives unique values for `segment`, `nurturing_stage`, and dealer-timezone
 * `test_date` + `last_retry_date` options (display matches grid; values are raw
 * API timestamps), manages `metadataFilters` ready for
 * {@link DataGridTypes.TEST_NURTURING_JOURNEYS_LEADS}.
 *
 * Mirrors the structure of {@link useEmailTemplatesFilterTray} so the
 * surrounding FilterTrayMAIQ wiring stays interchangeable across grids.
 */
export function useTestNurturingJourneysFilterTray({
  dealerId,
  opportunityType = 'test',
  fetchEnabled = false,
}: UseTestNurturingJourneysFilterTrayArgs): UseTestNurturingJourneysFilterTrayResult {
  const [selectedByCategory, setSelectedByCategory] =
    useState<FilterTraySelection>({});
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);

  const metadataFiltersForFetch = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(selectedByCategory)) {
      if (Array.isArray(v) && v.length > 0) out[k] = v;
    }
    return out;
  }, [selectedByCategory]);

  const params = useMemo(() => {
    const filterParams =
      Object.keys(metadataFiltersForFetch).length > 0
        ? Object.fromEntries(
            Object.entries(metadataFiltersForFetch)
              .filter(([, v]) => Array.isArray(v) && v.length > 0)
              .map(([k, v]) => [k, (v as string[]).join(',')]),
          )
        : {};
    return {
      dealer_id: dealerId,
      opportunity_type: opportunityType,
      limit: FILTER_TRAY_PAGE_SIZE,
      ...filterParams,
    };
  }, [dealerId, opportunityType, metadataFiltersForFetch]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['test-nurturing-journeys-filter-tray', dealerId, params],
    queryFn: () =>
      fetchDataFromAPI(JOURNEYS_ENDPOINT, params, 1, FILTER_TRAY_PAGE_SIZE),
    enabled: Boolean(dealerId) && fetchEnabled,
    staleTime: 5 * 60 * 1000,
  });

  const valueMaps = useMemo(
    () => deriveValueMaps(data?.body?.data ?? []),
    [data],
  );

  const categories: FilterTrayCategory[] = useMemo(
    () =>
      JOURNEY_FIELD_KEYS.filter(
        id =>
          (valueMaps.valuesByField[id]?.size ?? 0) > 0 ||
          (selectedByCategory[id] ?? []).length > 0,
      ).map(id => ({
        id,
        label: JOURNEY_FIELD_LABELS[id],
        selectedCount: (selectedByCategory[id] ?? []).length,
      })),
    [selectedByCategory, valueMaps],
  );

  const optionsForActiveCategory: FilterTrayOption[] = useMemo(() => {
    if (!activeCategoryId) return [];
    const key = activeCategoryId as JourneyFieldKey;
    if (!JOURNEY_FIELD_KEYS.includes(key)) return [];

    const fromData = Array.from(valueMaps.valuesByField[key] ?? []);
    const applied = (selectedByCategory[key] ?? []).filter(v =>
      fromData.includes(v),
    );
    const combined = Array.from(new Set([...fromData, ...applied]));

    const labelMap = valueMaps.labelByValue[key];
    const formatted = combined.map(value => ({
      value,
      label: labelMap?.get(value) ?? value,
    }));

    if (key === 'test_date' || key === 'last_retry_date') {
      formatted.sort((a, b) => {
        const aMs = moment(a.value).valueOf();
        const bMs = moment(b.value).valueOf();
        if (!Number.isFinite(aMs) && !Number.isFinite(bMs)) return 0;
        if (!Number.isFinite(aMs)) return 1;
        if (!Number.isFinite(bMs)) return -1;
        return bMs - aMs;
      });
    } else {
      formatted.sort((a, b) => a.label.localeCompare(b.label));
    }
    return formatted;
  }, [activeCategoryId, valueMaps, selectedByCategory]);

  const onSelectionChange = useCallback(
    (categoryId: string, selectedValues: string[]) => {
      setSelectedByCategory(prev => ({
        ...prev,
        [categoryId]: selectedValues,
      }));
    },
    [],
  );

  const onClearAllFilters = useCallback(() => {
    setSelectedByCategory({});
  }, []);

  const onClearActiveCategory = useCallback(() => {
    if (!activeCategoryId) return;
    setSelectedByCategory(prev => ({ ...prev, [activeCategoryId]: [] }));
  }, [activeCategoryId]);

  const onClearAllInPanel = useCallback(() => {
    setSelectedByCategory({});
  }, []);

  const metadataFilters = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(selectedByCategory)) {
      if (Array.isArray(v) && v.length > 0) out[k] = v;
    }
    return out;
  }, [selectedByCategory]);

  const totalActiveCount = useMemo(
    () =>
      Object.values(selectedByCategory).reduce(
        (sum, arr) => sum + (arr?.length ?? 0),
        0,
      ),
    [selectedByCategory],
  );

  return {
    categories,
    optionsForActiveCategory,
    selectedByCategory,
    onSelectionChange,
    onClearAllFilters,
    onClearActiveCategory,
    onClearAllInPanel,
    metadataFilters,
    totalActiveCount,
    activeCategoryId,
    setActiveCategoryId,
    isLoading: !!isLoading || !!isFetching,
    error: error as Error | null,
  };
}
